import { useCallback, useState } from "react";

import { isAPIError } from "@/core/services";
import {
  definirEquipeObra,
  readDivergenciasAlocacao,
  readEquipeObraErrors,
} from "./equipe-obra.gateway";
import type {
  DivergenciasAlocacao,
  DefinirEquipeObraParams,
  EquipeObraAlocacaoInput,
  EquipeObraCampo,
} from "./equipe-obra.types";

export const EQUIPE_OBRA_FORBIDDEN_MESSAGE = "Acesso não autorizado";
export const EQUIPE_OBRA_ERROR_MESSAGE = "Não foi possível salvar a equipe. Tente novamente.";
export const EQUIPE_OBRA_NOT_FOUND_MESSAGE = "Obra não encontrada.";
export const EQUIPE_OBRA_CONFLICT_MESSAGE = "A equipe desta obra já foi definida.";

export type DefinirEquipeObraResult =
  | { ok: true }
  | {
      ok: false;
      /** 403 — a tela mostra Toast "Acesso não autorizado". */
      forbidden: boolean;
      /** 409 / 404 / falha inesperada — mensagem geral. */
      message: string | null;
      /** 400 — erros por campo, indexados pela posição da alocação enviada. */
      fieldErrors: Map<number, Partial<Record<EquipeObraCampo, string>>>;
      /** 409 `DIVERGENCIAS_ALOCACAO` — conflitos/indisponibilidades apontados pelo servidor. */
      divergencias: DivergenciasAlocacao | null;
    };

function failure(patch: Partial<Extract<DefinirEquipeObraResult, { ok: false }>>) {
  return {
    ok: false as const,
    forbidden: false,
    message: null,
    fieldErrors: new Map(),
    divergencias: null,
    ...patch,
  };
}

/** Nunca rejeita: devolve o resultado para a tela decidir onde mostrar o erro. */
export function useDefinirEquipeObra() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const definir = useCallback(
    async (
      obraId: string,
      alocacoes: EquipeObraAlocacaoInput[],
      justificativa?: string,
    ): Promise<DefinirEquipeObraResult> => {
      const params: DefinirEquipeObraParams = justificativa
        ? { obraId, alocacoes, justificativa }
        : { obraId, alocacoes };
      setIsSubmitting(true);

      try {
        await definirEquipeObra(params);
        return { ok: true };
      } catch (error) {
        if (!isAPIError(error)) return failure({ message: EQUIPE_OBRA_ERROR_MESSAGE });

        if (error.status === 403) return failure({ forbidden: true });
        if (error.status === 404) {
          return failure({ message: error.message || EQUIPE_OBRA_NOT_FOUND_MESSAGE });
        }
        if (error.status === 409) {
          const divergencias = readDivergenciasAlocacao(error);
          if (divergencias) return failure({ divergencias });
          return failure({ message: error.message || EQUIPE_OBRA_CONFLICT_MESSAGE });
        }

        if (error.status === 400) {
          const { byIndex, general } = readEquipeObraErrors(error);
          if (byIndex.size > 0 || general.length > 0) {
            return failure({ fieldErrors: byIndex, message: general[0] ?? null });
          }
          return failure({ message: error.message || EQUIPE_OBRA_ERROR_MESSAGE });
        }

        return failure({ message: EQUIPE_OBRA_ERROR_MESSAGE });
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { definir, isSubmitting };
}
